import React, {ReactNode, useEffect, useRef, useState, useMemo} from 'react';
import classnames from 'classnames';
import {AgGridReact} from 'ag-grid-react';
import {ColDef, GridOptions} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.min.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DodExcelLayoutPreview.scss';
import {DodCharacteristicFilter, DodCharacteristicFilterMap, DodColDef, DodDimension, DodDimensionType, DodFilter, DodFilters, DodPpgFilter, DodPpgFilterMap, DodRowDef, TimePeriodValue} from '@/types/DodRun';
import {ByzzerMessagePopUp} from '@byzzer/ui-components';
import {DodPanel} from '@/components/DodConfigEditor/common/DodPanel';
import {uniq} from 'lodash';
import {format as formatDate} from "date-fns";
import {ErrorBoundary} from "react-error-boundary";
import {useDodWizard} from "@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext";
import {caseInsensitiveSort, caseInsensitiveReverseSort} from "@/utils/Sort";
import cartesian from 'cartesian';
import {TimePeriodRange} from "@/utils/timePeriod/TimePeriodRange";
import { useApp } from "@/contexts/UserContext";
import { factSetToCoreDisplayNames, factSetToDisplayNames, factSetToSharedDisplayNames } from '@/components/DodConfigEditor/common/utils';
import { RunConfigMarket } from '@/types/ReportRun';
import { useMarketService } from '@/services/market.service';

const baseClassName = 'dod-excel-layout-preview';

export type DodExcelLayoutPreviewProps = {
    className?: string;
    name?: string;
    title?: string;
    maxSampleSize?: number;
}

const gridOptions: GridOptions = {
    rowHeight: 25,
    suppressAnimationFrame: true,
    animateRows: false,
    suppressCellFocus: true,
    suppressRowHoverHighlight: true,
    headerHeight: 30,
    components: {
        agColumnHeader: PreviewGridHeader,
    },
    defaultColDef: {
        wrapText: false,
        wrapHeaderText: true,
        autoHeight: true,
        autoHeaderHeight: true,
    }
};

export const SUB_DIMENSION_VALUE_TO_DISPLAY: Record<DodDimension | string, string> = {
    departments: 'Departments',
    superCategories: 'Super Category',
    categories: 'Category',
    subcategories: 'Subcategory',
    upcs: 'UPC',
    productDescriptions: 'Product Description',
    parentCompanies: 'Parent Company',
    brands: 'Brand',
    manufacturers: 'Manufacturer',
    markets: 'Markets',
    timePeriods: 'Time Periods',
    facts: 'Facts',
    date: 'Date'
};

interface PreviewColDef extends ColDef {
};

type PreviewRow = Record<string, string>;

interface StackedDimension {
    dim: string;
    value: string;
}

type DimensionValueMap = Record<string, string[] | StackedDimension[]>;

const FILTER_TO_DIMENSION: Record<string, DodDimension> = {
    markets: 'markets',
    timePeriods: 'timePeriods',
    facts: 'facts',
};

const EXCEL_COLUMNS_LIMIT = 16384;

interface DimensionComparator {
    (a: string, b: string, values: string[]): number;
}

const comparatorMap: Record<string, DimensionComparator> = {}
interface PreviewGridState {
    colDefs: ColDef[];
    rowData: Record<string, string | StackedDimension>[];
}

export function DodExcelLayoutPreview({
                                          className,
                                          title = 'Preview',
                                          maxSampleSize = 5,
                                      }: DodExcelLayoutPreviewProps) {

    const { maxDataDates } = useApp();
    const maxDate = useMemo<string>(() => {
        return formatDate(maxDataDates.rms!, 'yyyyMMdd');
    }, [maxDataDates]);

    const {runConfig, setIsColumnsExceedingLimit, allExceptSelectionFields, rowColConfigs} = useDodWizard();
    const { getMarketTypes, getMarketChannels, getChildMarketTypes, getMarketParentCompanies } = useMarketService();

    /**
     * Resolves the full list of option values for a market characteristic so that the preview can
     * expand an 'all' selection into the actual values from dim_market (e.g. all channels) instead
     * of showing the literal word "All". Mirrors the option sources used by the market characteristic
     * value picker (DodMarketFilterValuePicker). Returns an empty array when the characteristic has no
     * synchronously-resolvable options.
     */
    function getMarketCharacteristicOptions(characteristic: string): string[] {
        switch (characteristic) {
            case 'marketType':
                return getMarketTypes();
            case 'channel':
                return getMarketChannels();
            case 'childMarketType':
                return getChildMarketTypes();
            case 'parentRetailer':
            case 'parentCompany':
                return getMarketParentCompanies();
            case 'marketLevel':
                return ['Parent Market', 'Child Market'];
            case 'marketTier':
                return ['National', 'Regional'];
            default:
                return [];
        }
    }
    // this is to test if a single unified value improves performance
    const [gridState, setGridState] = useState<PreviewGridState>({
        colDefs: [],
        rowData: [],
    })
    const [rowData, setRowData] = useState<Record<string, string | StackedDimension>[]>([])
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
    const [pageTitles, setPageTitles] = useState<ReactNode[]>([]);
    const gridRef = useRef<AgGridReact>(null);

    function computeTotalColumns(cols: DodColDef[]): number {    
        const getCountFromFilter = (
            filter: DodFilter<string[] | 'all'> | DodFilter<RunConfigMarket[]> | DodFilter<TimePeriodValue[]>
        ): number => {
            if (!filter) return 1;
            const { values, summedSelections } = filter;
            if (values === 'all') {
                return 1;
            } else {
                const valCount = Array.isArray(values) ? values.length : 0;
                const sumCount = Array.isArray(summedSelections) ? summedSelections.length : 0;
                return (valCount + sumCount) || 1;
            }
        };
    
        const getCountFromFilterMap = (filterMap: DodCharacteristicFilterMap | DodPpgFilterMap): number => {
            let total = 0;
            for (const key of Object.keys(filterMap)) {
                const filter = filterMap[key];
                if (filter.values === 'all') {
                    total += 1;
                } else {
                    const valCount = Array.isArray(filter.values) ? filter.values.length : 0;
                    const sumCount = Array.isArray(filter.summedSelections) ? filter.summedSelections.length : 0;
                    total += (valCount + sumCount);
                }
            }
            return total > 0 ? total : 1;
        };
    
        // A helper to find a characteristic/customCharacteristic/ppg filter by displayName
        const findCharacteristicLikeFilterCount = (dim: string): number | null => {
            // characteristics
            const charMap = runConfig.filters.characteristics;
            for (const key in charMap) {
                const filter = charMap[key];
                if (filter.displayName === dim) {
                    return getCountFromFilter(filter);
                }
            }
    
            // customCharacteristics
            const customCharMap = runConfig.filters.customCharacteristics;
            for (const key in customCharMap) {
                const filter = customCharMap[key];
                if (filter.displayName === dim) {
                    return getCountFromFilter(filter);
                }
            }
    
            // ppgs
            const ppgMap = runConfig.filters.ppgs;
            for (const key in ppgMap) {
                const filter = ppgMap[key];
                if (filter.displayName === dim) {
                    return getCountFromFilter(filter);
                }
            }

            // marketCharacteristics
            const marketCharMap = runConfig.filters?.marketCharacteristics;
            if (marketCharMap) {
                for (const key in marketCharMap) {
                    const filter = marketCharMap[key];
                    if (filter?.displayName === dim) {
                        return getCountFromFilter(filter);
                    }
                }
            }
    
            // No match found
            return null;
        };
    
        let totalColumns = 1;
    
        cols.forEach(col => {
            let count = 1;
            const { dim, order } = col;
    
            if (dim in runConfig.filters) {
                // Direct match in filters (like categories, markets, timePeriods, etc.)
                const filterEntry = runConfig.filters[dim as keyof DodFilters] as any;
    
                // If this entry is a normal DodFilter:
                if (filterEntry && 'values' in filterEntry && 'summedSelections' in filterEntry) {
                    // Regular DodFilter
                    count = getCountFromFilter(filterEntry);
                } else if (dim === 'characteristics' || dim === 'customCharacteristics' || dim === 'ppgs') {
                    // Not expected here directly since we handle them by displayName. 
                    // If encountered, just treat as map.
                    count = getCountFromFilterMap(filterEntry);
                } else {
                    // If somehow not matching expected structures, fallback to 1
                    count = 1;
                }
    
            } else {
                // No direct match in filters
                // Try to match by displayName in characteristics/customCharacteristics/ppgs
                const charCount = findCharacteristicLikeFilterCount(dim);
                if (charCount !== null) {
                    // Found a characteristic-like filter by displayName
                    count = charCount;
                } else {
                    // Not found in filters or characteristic-like maps, fall back to order or 1
                    if (order && order.length > 0) {
                        count = order.length;
                    } else {
                        count = 1;
                    }
                }
            }
    
            totalColumns *= count;
        });
    
        return totalColumns;
    }
    
    useEffect(() => {
        try {
            // don't bother doing anything if the dimensionValues haven't been populated
            const dimensionValues = generateDimensionValues();
            let rows = runConfig.layout.rows;
            let cols = runConfig.layout.columns;
            const rowTypeOrder: DodDimensionType[] = uniq(rows.map(v => v.type));
            // if Extract Date is clicked, then date field will be added in row/column based on timeperiod dimension selection
            if(runConfig.layout.includeExtractDate){
                const timePeriodIndex = rows.findIndex(row => row.type === "time_periods");
                const dateRowIndex = rows.findIndex(row => row.type === "date");

                if(timePeriodIndex !== -1 && dateRowIndex === -1){
                    rows.splice(timePeriodIndex+1, 0, {
                        "type": "date",
                        "dim": "date",
                        "sortType": "manual",
                        "hide": false,
                        "pageBy": false,
                        "stack": false,
                        "order": []
                    });
                }

                const timePeriodColIndex = cols.findIndex(row => row.type === "time_periods");
                const dateColIndex = cols.findIndex(row => row.type === "date");

                if(timePeriodColIndex !== -1 && dateColIndex === -1){
                    cols.splice(timePeriodColIndex+1, 0, {
                        "type": "date",
                        "dim": "date",
                        "sortType": "manual",
                        "hide": false,
                        "pageBy": false,
                        "stack": false,
                        "order": []
                    });
                }
            }else {
                // if unchecked 
                const timePeriodIndex = rows.findIndex(row => row.type === "date");
                if(timePeriodIndex !== -1) rows.splice(timePeriodIndex, 1);
                const timePeriodColIndex = cols.findIndex(row => row.type === "date");
                if(timePeriodColIndex !== -1) cols.splice(timePeriodColIndex, 1);
            }
            // market characteristics are only previewed when the user has actually selected
            // characteristic values. The wizard keeps a placeholder dim ('marketCharacteristics')
            // in the layout so the dimension group still renders in the DYL, but it has no
            // preview values, so drop those entries to avoid empty/undefined columns.
            const hasMarketCharValues = (dim: string): boolean =>
                Array.isArray(dimensionValues[dim]) && (dimensionValues[dim] as unknown[]).length > 0;
            rows = rows.filter(row => row.type !== 'market_characteristics' || hasMarketCharValues(row.dim));
            cols = cols.filter(col => col.type !== 'market_characteristics' || hasMarketCharValues(col.dim));
            const dimensionHeaders: PreviewColDef[] = getRegularColDefs(rows);
            // if any products are stacked we need to add the columns for that
            if (rows.some(v => v.stack)) {
                // stacked products and details must always follow the
                // @ts-ignore
                let insertionPosition = dimensionHeaders.findLastIndex(v => v.type === 'products') + 1;
                if (insertionPosition === 0 && rowTypeOrder.length > 1) {
                    insertionPosition = rowTypeOrder.indexOf('products');
                }
                dimensionHeaders.splice(insertionPosition, 0, {
                        headerName: 'Stacked Products',
                        type: 'stackedProducts',
                        field: 'stackedProducts.value',
                    },
                    {
                        headerName: 'Stacked Product Details',
                        type:'stackedProductDetails',
                        field: 'stackedProducts.dim',
                    });
            }

            const aggregatedHeadings: PreviewColDef[] = getAggregateColDefs(cols, dimensionValues);
            const pageTitles = getPageTitles(rows, cols, dimensionValues);
            setPageTitles(pageTitles);

            const colDefs: ColDef[] = [
                // limit to 100 columns
                ...[...dimensionHeaders, ...aggregatedHeadings].slice(0, 20),
                // make sure table is always filled horizontally
                {flex: 1}
            ];
            setIsColumnsExceedingLimit(computeTotalColumns(cols) >= EXCEL_COLUMNS_LIMIT);
            const rowData: PreviewRow[] = generateRowData(dimensionHeaders, dimensionValues).slice(0, 100);
            setColumnDefs(colDefs);
            setRowData(rowData);

        } catch (err) {
            console.error(err)
        }
    }, [runConfig]);

    function generateDimensionValues(): DimensionValueMap {
        let valueMap: DimensionValueMap = {};
        const allExceptSelectionFieldsSet = new Set(allExceptSelectionFields);
        try {
            // loop through all of the filters and facts and grab the first 5 values to use as preview of the real data
            for (let key in runConfig.filters) {
                const valueKey = FILTER_TO_DIMENSION[key] ?? key;
                switch (key) {
                    case 'markets': {
                        const {values, summedSelections} = runConfig.filters[key];
                        valueMap[valueKey] = [
                            ...values.slice(0, maxSampleSize).map((market) => market.name),
                            ...summedSelections.slice(0, maxSampleSize).map(sum => sum.name)
                        ].sort(caseInsensitiveSort)
                        break;
                    }
                    case 'timePeriods': {
                        const { values, summedSelections } = runConfig.filters[key];
                        const tpConfig = rowColConfigs.find(c => c.type === 'time_periods');
                        const tpSortType = tpConfig?.sortType ?? 'default';

                        let timePeriodValues: string[];
                        if (tpSortType === 'manual' && tpConfig?.values?.length) {
                            // use the user's dragged order directly — already in legacy string format
                            timePeriodValues = tpConfig.values.filter(v => !summedSelections.some(ss => ss.name === v));
                        } else {
                            const tpComparator = tpSortType === 'desc' ? TimePeriodRange.compareDesc : TimePeriodRange.compareAsc;
                            timePeriodValues = values.flat()
                                .map((tp) => new TimePeriodRange(tp, maxDate))
                                .toSorted(tpComparator)
                                .map((tp) => tp.toLegacyDodString());
                        }

                        valueMap[valueKey] = [
                            ...timePeriodValues.slice(0, maxSampleSize),
                            ...summedSelections.map((sum) => sum.name),
                        ];
                        valueMap['date'] = [
                            ...timePeriodValues.slice(0, maxSampleSize).map(legacyStr => {
                                const tpRaw = values.flat().find(v => {
                                    try { return new TimePeriodRange(v, maxDate).toLegacyDodString() === legacyStr; } catch { return false; }
                                });
                                if (!tpRaw) return legacyStr;
                                try { return new TimePeriodRange(tpRaw, maxDate).toLegacyDodDateString(); } catch { return legacyStr; }
                            }),
                            ...summedSelections.map((sum) => sum.name),
                        ];
                        break;
                    }
                    case 'marketCharacteristics': {
                        Object.values<DodCharacteristicFilter>(runConfig.filters[key]).forEach(
                            (filter) => {
                                let { displayName, values, summedSelections, characteristic } = filter;
                                // When 'All' is selected, expand to the actual market characteristic
                                // options (from dim_market) so the preview reflects real values.
                                if (values === 'all') {
                                    const allOptions = getMarketCharacteristicOptions(characteristic);
                                    values = allOptions.length ? allOptions : ['All'];
                                }

                                // Find the rowColConfig for this market characteristic to apply sort order
                                const mcConfig = rowColConfigs.find(c => c.type === 'market_characteristics' && c.dim === displayName);
                                const mcSortType = mcConfig?.sortType ?? 'default';
                                let marketCharValues = values;

                                if (mcSortType === 'manual' && mcConfig?.values?.length) {
                                    // use the user's dragged order directly
                                    marketCharValues = mcConfig.values.filter(v => !summedSelections.some(ss => ss.name === v));
                                } else if (mcSortType === 'asc') {
                                    marketCharValues = [...values].sort(caseInsensitiveSort);
                                } else if (mcSortType === 'desc') {
                                    marketCharValues = [...values].sort(caseInsensitiveReverseSort);
                                }
                                // else: 'default' uses values as-is

                                if (allExceptSelectionFieldsSet.has(characteristic)) {
                                    valueMap[displayName] = [
                                        ...marketCharValues.slice(0, maxSampleSize).map(val => `All Except ${val}`),
                                        ...summedSelections.slice(0, maxSampleSize).map((sum) => `All Except ${sum.name}`),
                                    ];
                                } else {
                                    valueMap[displayName] = [
                                        ...marketCharValues.slice(0, maxSampleSize),
                                        ...summedSelections.slice(0, maxSampleSize).map((sum) => sum.name),
                                    ];
                                }
                            }
                        );
                        break;
                    }
                    case 'ppgs':
                    case 'characteristics':
                    case 'customCharacteristics': {
                        Object.values<DodCharacteristicFilter | DodPpgFilter>(runConfig.filters[key]).forEach(
                            (filter) => {
                                let { displayName, values, summedSelections } = filter;
                                if (values === 'all') {
                                    values = ['All'];
                                }
                                if (allExceptSelectionFieldsSet.has(filter.characteristic ? filter.characteristic : filter.ppg)) {
                                    valueMap[displayName] = [
                                        ...[...values.slice(0, maxSampleSize)].map(val => `All Except ${val}`),
                                        ...[...summedSelections.slice(0, maxSampleSize).map((sum) => sum.name)].map(val => `All Except ${val}`),
                                    ];
                                } else {
                                    valueMap[displayName] = [
                                        ...values.slice(0, maxSampleSize),
                                        ...summedSelections.slice(0, maxSampleSize).map((sum) => sum.name),
                                    ];
                                }

                            }
                        );
                        break;
                    }
                    default: {
                        let { values, summedSelections} = runConfig.filters[key];
                        if (values === 'all') {
                            values = ['All']
                        }
                        // Below condition is to check if this particular filter is in all except field or not
                        if (allExceptSelectionFieldsSet.has(key)) {
                            valueMap[valueKey] = [
                                ...[...values.slice(0, maxSampleSize)].map(val => `All Except ${val}`),
                                ...[...summedSelections.slice(0, maxSampleSize).map(sum => sum.name)].map(val => `All Except ${val}`)
                            ]
                        } else {
                            valueMap[valueKey] = [
                                ...values.slice(0, maxSampleSize),
                                ...summedSelections.slice(0, maxSampleSize).map(sum => sum.name)
                            ]
                        }

                    }
                }
            }

            // make sure we have values for product descriptions if it was auto-included b/c of upcs
            if (valueMap.upcs.length && !valueMap.productDescriptions.length) {
                valueMap.productDescriptions = valueMap.upcs;
            }

            if (runConfig.facts.length) {
                valueMap.facts = runConfig.facts.map(factSetToDisplayNames).flat().slice(0, maxSampleSize).sort(caseInsensitiveSort);

                // BYZ-12122 - had to explicitly map for core and share facts and remove caseInsensitiveSort to maintain
                // the same order as in facts tab preview
                // const coreFacts: string[] = runConfig.facts.map(factSetToCoreDisplayNames).flat();
                // const shareFacts: string[] = runConfig.facts.map(factSetToSharedDisplayNames).flat();
                // valueMap.facts = [...coreFacts, ...shareFacts].slice(0, maxSampleSize);
            }

            // stacked product details needs to include the names of the non-stacked product sub dimensions
            let stackedDetailPrefix = runConfig.layout.rows
                .filter(row => !row.stack && row.type === 'products')
                .map(row => SUB_DIMENSION_VALUE_TO_DISPLAY[row.dim] ?? row.dim)
                .join(' | ');
            if(stackedDetailPrefix) {
                stackedDetailPrefix = `${stackedDetailPrefix} | `
            }

            valueMap.stackedProducts = runConfig.layout.rows
                .filter(v => v.stack)
                .map(v => v.dim)
                .map(dim => valueMap[dim].map(value => ({
                    dim: `${stackedDetailPrefix}${SUB_DIMENSION_VALUE_TO_DISPLAY[dim] ?? dim}`,
                    value
                }))).flat();

        } catch (err) {
            console.error(err);
        }
        return valueMap;
    }

    /**
     * Produces an array that is the cartesian product of the dimension's values
     * @param dimensionNames Array of dimension names
     * @param dimensionValues
     */
    function generateCombinations(dimensionNames: string[], dimensionValues: DimensionValueMap): PreviewRow[] {

        // reduce the array dimension names to a map with keys for each dimension's values
        const map = dimensionNames.reduce((map, dim) => ({
            ...map,
            [dim]: dimensionValues[dim]
        }), {});

        return cartesian(map);
    }

    function getRegularColDefs(rows: DodRowDef[]): PreviewColDef[] {

        const defs = rows.filter(v => !v.hide && !v.stack)
            .map(({dim, type}: DodRowDef) => ({
                headerName: SUB_DIMENSION_VALUE_TO_DISPLAY[dim] ?? dim,
                type,
                field: dim
            }));

        return defs;
    }

    function getAggregateColDefs(columns: DodColDef[], dimensionValues: DimensionValueMap): PreviewColDef[] {

        const dimensions: string[] = columns.filter(v => !v.hide).map(v => v.dim);
        const isDateField = dimensions.find(v => v === 'date');
        return generateCombinations(dimensions.filter(v => v !== 'date'), dimensionValues).map((group: PreviewRow) => {
            return dimensions.map(dimension => {
                if(isDateField && dimension === "date"){
                    const index = dimensionValues.timePeriods.findIndex(val => val === group["timePeriods"])
                    return `${dimensionValues['date'][index]}`;
                }
                return group[dimension]
            }).join('\n')
        }).map(headerName => ({
            headerName,
            type: 'aggregated',
            field: 'aggregated',
        }));
    }

    function generateRowData(cols: ColDef[], dimensionValues: DimensionValueMap): PreviewRow[] {
        // find all of the non-stacked dimensions
        const dimensionColumns: string[] = cols
            .filter(col => [
                'products',
                'markets',
                'time_periods',
                'facts',
                'market_characteristics'
            ].includes(col.type as string))
            .map(v => v.field!);

        // if we have stacked dimensions make sure they're included in the data
        if (cols.some(col => col.type === 'stackedProducts')) {
            dimensionColumns.push('stackedProducts')
        }
        let data: PreviewRow[] = generateCombinations(dimensionColumns, dimensionValues);
        if(cols.find(c => c.type == "date")){
            data = data.map(d => {
                const index = dimensionValues.timePeriods.findIndex(val => val === d.timePeriods);
                d.date = dimensionValues['date'][index] ?? "";
                return d;
            })
        }
        return data;    
    }

    function getPageTitles(rows, columns, dimensionValues: DimensionValueMap): ReactNode[] {

        const pagedDims: string[] = [
            ...rows.filter((rowItem) => rowItem.pageBy),
            ...columns.filter((columnItem) => columnItem.pageBy),
        ].map((item) => item.dim);

        let pageByGroups: PreviewRow[] = generateCombinations(pagedDims, dimensionValues);

        return pageByGroups.map(group => (<>
            {pagedDims.map(dim => <p key={dim}>{group[dim]}</p>)}
        </>));
    }

    function handleDataChanged() {
        // this has to be triggered after everything else finishes,
        // the 0 second setTimeout pushes this to end of the queue
        setTimeout(resizeColumns, 0);
    }

    function resizeColumns() {
            const columnApi = gridRef.current?.columnApi;

            if (!columnApi) return;

            // we want to ressize all but the last flex column
            const columnIds = columnApi.getColumns()?.slice(0, -1).map(column => column.getId())
            if (columnIds?.length) {
                columnApi.autoSizeColumns(columnIds);
            }
    }

    function handleError(err) {
        console.error(err);
    }

    return (
        <ErrorBoundary fallback={<>Preview Failed</>} onError={handleError}>
            <DodPanel
                className={classnames(baseClassName, className)}
                expandable={true}
                name={'dod-layout-preview'}
                title={title}
                enableFilter={false}
            >
                <AgGridReact
                    ref={gridRef}
                    className={classnames('ag-theme-alpine', `${baseClassName}__preview-table`)}
                    gridOptions={gridOptions}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    onRowDataUpdated={handleDataChanged}
                    overlayNoRowsTemplate="No preview rows available."
                    onFirstDataRendered={resizeColumns}
                />
                <div className={`${baseClassName}__tabs`}>
                    {pageTitles.map((title, index) => (
                        <div className={`${baseClassName}__tab`} key={index}>
                            <ByzzerMessagePopUp tipDelay={[500, 0]} tip={title} tipLocation="top-start">
                                Page {index + 1}
                            </ByzzerMessagePopUp>
                        </div>
                    ))}
                </div>
            </DodPanel>
        </ErrorBoundary>
    );
}

function isCharacteristic(type: string): boolean {
    return ['characteristics', 'customCharacteristics'].includes(type);
}

function PreviewGridHeader({displayName}) {

    const displayNames = displayName.split('\n');

    if (displayNames.length > 1) {
        return <div className={`${baseClassName}__header--multi-dim`}>
            {displayNames.map(name => <div key={name} className={`${baseClassName}__header-dim`}>{name}</div>)}
        </div>
    }

    return displayName;
}

export default DodExcelLayoutPreview;
